// packages/base/src/response-view/layout-intent.ts
//
// LV1-S1 — Layout-intent DSL: behavioural surface.
//
// The TypeBox schemas + `Static` types are the single source of truth and live
// in `../api-schemas/views.ts`. This module owns the behaviour layered on top
// of that contract: the byte-identical-to-today default intent factory and the
// strict json-schema export the LV1-S2 compiler hands to the chat model. Pure
// `base` — no vscode/react/fastify/native imports.
//
// Result<T,E> note (for review): neither `defaultLayoutIntent()` nor the
// `LAYOUT_INTENT_JSON_SCHEMA` constant is a fallible cross-boundary call — the
// factory is a total, pure constructor that cannot fail, and the json-schema is
// a deterministically-derived constant. The CLAUDE.md "public functions return
// Result<T,E>" invariant binds the fallible LV1-S2 NL→intent compiler (which
// hits the model and CAN fail), not these pure constants. Wrapping a constant
// factory in `Result` would add a never-`err` branch with no caller value.
import { LayoutIntentSchema } from '../api-schemas/views.js';
/**
 * The byte-identical-to-today baseline intent: `RIGHT` / `layered` / `normal`
 * with empty pins/groups/emphasis and `unsupported:false`. This mirrors the
 * hard-coded ELK defaults in `layout-builder.ts` (`ROOT_OPTS` direction
 * `RIGHT`, algorithm `layered`), so a missing or blank intent renders exactly
 * as today.
 *
 * Returns a FRESH object (with fresh arrays) on every call — there is no shared
 * mutable singleton, so a caller that mutates `pins`/`groups`/`emphasis` on one
 * default cannot poison the next caller's default.
 */
export function defaultLayoutIntent() {
    return {
        direction: 'RIGHT',
        algorithm: 'layered',
        spacing: 'normal',
        pins: [],
        groups: [],
        emphasis: [],
        deemphasis: [],
        unsupported: false,
        notes: '',
    };
}
/**
 * Recursively deep-clone a JSON-schema value while dropping every `$id` key.
 * OpenAI/Azure **strict** `json_schema` structured output rejects schemas that
 * carry `$id`/`$schema`/`$defs`-by-ref drift, so the strict export must be
 * `$id`-free at every level. Deriving it from the TypeBox SSOT (rather than
 * hand-authoring a second copy) guarantees it can never drift from
 * `LayoutIntentSchema`.
 */
function stripSchemaIds(node) {
    if (Array.isArray(node)) {
        return node.map((child) => stripSchemaIds(child));
    }
    if (node !== null && typeof node === 'object') {
        const source = node;
        const out = {};
        for (const key of Object.keys(source)) {
            if (key === '$id')
                continue;
            out[key] = stripSchemaIds(source[key]);
        }
        return out;
    }
    return node;
}
/**
 * The strict-structured-output JSON schema for a `LayoutIntent`, derived
 * deterministically from `LayoutIntentSchema` by deep-cloning and recursively
 * stripping all `$id` keys. Suitable as the `json_schema` payload for an
 * OpenAI/Azure strict structured-output call (LV1-S2): `additionalProperties`
 * is `false` at every object level and `required` lists every property, both
 * inherited from the TypeBox SSOT (every field is non-optional there).
 *
 * Frozen so a downstream consumer cannot accidentally mutate the shared export.
 */
export const LAYOUT_INTENT_JSON_SCHEMA = Object.freeze(stripSchemaIds(
// Clone first so we never mutate the live TypeBox schema object.
JSON.parse(JSON.stringify(LayoutIntentSchema))));
//# sourceMappingURL=layout-intent.js.map